import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { LaptopIcon, MonitorIcon, PencilIcon, ShieldOffIcon, SmartphoneIcon } from "lucide-react";
import { useState, type SubmitEvent } from "react";
import { Confirm } from "@/components/confirm";
import { EmptyState, ErrorText, PageHeader } from "@/components/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/icon-button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { fmtDateTime, platformLabel, timeAgo } from "@/lib/format";
import { useDevices } from "@/lib/hooks";

export const Route = createFileRoute("/_app/devices/")({ component: DevicesPage });

function DevicesPage() {
  const devices = useDevices();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const rename = useMutation({
    mutationFn: (input: { device_id: string; name: string }) => api.devices.rename(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["devices"] });
      setRenaming(null);
    },
  });
  const revoke = useMutation({
    mutationFn: (device_id: string) => api.devices.revoke({ device_id }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["devices"] }),
  });
  const Icon = (p: string) => (p === "browser" ? MonitorIcon : p === "macos" ? LaptopIcon : SmartphoneIcon);
  return (
    <>
      <PageHeader
        title="Devices"
        description="Every device holds its own copy of the vault key. Revoking removes its access to new changes."
      />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-3 lg:col-span-2">
          {devices.data?.length === 0 ? <EmptyState title="No devices" /> : null}
          {devices.data?.map((d) => {
            const I = Icon(d.platform);
            return (
              <div
                key={d.id}
                className="flex items-center gap-3 rounded-xl border bg-card p-4"
                data-testid="device-row"
              >
                <I className="size-5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 font-medium">
                    {d.name}
                    {d.current ? <Badge variant="secondary">this browser</Badge> : null}
                  </p>
                  <p className="text-xs text-muted-foreground" title={`Enrolled ${fmtDateTime(d.created_at)}`}>
                    {platformLabel(d.platform)} · last seen {timeAgo(d.last_seen_at)} · key {d.public_key.slice(0, 10)}…
                  </p>
                </div>
                <IconButton label={`Rename ${d.name}`} onClick={() => setRenaming({ id: d.id, name: d.name })}>
                  <PencilIcon />
                </IconButton>
                <Confirm
                  trigger={
                    <IconButton
                      label={d.current ? "You cannot revoke this browser" : `Revoke ${d.name}`}
                      disabled={d.current}
                    >
                      <ShieldOffIcon />
                    </IconButton>
                  }
                  title={`Revoke ${d.name}?`}
                  description="Its sessions end immediately and it stops receiving changes. Files already on that device stay there."
                  actionLabel="Revoke"
                  destructive
                  onConfirm={() => revoke.mutateAsync(d.id).then(() => undefined)}
                />
              </div>
            );
          })}
          <ErrorText error={revoke.error} />
        </div>
        <Card className="h-fit">
          <CardHeader>
            <CardTitle>Approve a new device</CardTitle>
            <CardDescription>
              Run <code className="font-mono">envstash login</code> on the new Mac and enter the code it shows.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="flex gap-2"
              onSubmit={(e: SubmitEvent<HTMLFormElement>) => {
                e.preventDefault();
                void nav({ to: "/devices/enroll", search: { code } });
              }}
            >
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="ABCD-EFGH"
                className="font-mono tracking-wider"
                aria-label="Device code"
              />
              <Button type="submit" disabled={code.replace(/[^A-Z0-9]/gi, "").length < 8}>
                Continue
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
      <Dialog open={renaming !== null} onOpenChange={(o) => !o && setRenaming(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename device</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="device-name">Name</Label>
            <Input
              id="device-name"
              value={renaming?.name ?? ""}
              onChange={(e) => setRenaming((r) => (r ? { ...r, name: e.target.value } : r))}
            />
          </div>
          <ErrorText error={rename.error} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenaming(null)}>
              Cancel
            </Button>
            <Button
              disabled={rename.isPending || !renaming?.name.trim()}
              onClick={() => {
                if (renaming) rename.mutate({ device_id: renaming.id, name: renaming.name.trim() });
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
